import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  Tabs,
  Table,
  Tag,
  Space,
  Button,
  App,
  Tooltip,
  Popconfirm,
  Empty,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  LoadingOutlined,
  ReloadOutlined,
  ScheduleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { jobsApi, type JobItemDto, type JobsListRequest } from '@/api/jobsApi';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;

type StateKey = JobsListRequest['state'];

/**
 * Panel de monitoreo de Hangfire.
 *
 * Muestra estadisticas de los jobs (succeeded, failed, processing, etc.) y
 * permite operar sobre ellos: re-encolar fallidos, eliminar.
 *
 * Solo accesible para SuperAdmin (validado en el backend).
 */
export function JobsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const [activeState, setActiveState] = useState<StateKey>('failed');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const statsQuery = useQuery({
    queryKey: ['jobs', 'stats'],
    queryFn: jobsApi.getStats,
    refetchInterval: 5000,  // Refresh stats every 5s
  });

  const listQuery = useQuery({
    queryKey: ['jobs', 'list', activeState, page, pageSize],
    queryFn: () => jobsApi.list({ state: activeState, page, pageSize }),
  });

  const requeueMutation = useMutation({
    mutationFn: (id: string) => jobsApi.requeue(id),
    onSuccess: () => {
      message.success('Job re-encolado.');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (e: Error) => message.error('Error: ' + e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => jobsApi.delete(id),
    onSuccess: () => {
      message.success('Job eliminado.');
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
    },
    onError: (e: Error) => message.error('Error: ' + e.message),
  });

  const stats = statsQuery.data;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          <ThunderboltOutlined style={{ marginRight: 12 }} />
          Jobs (Hangfire)
        </Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => {
            statsQuery.refetch();
            listQuery.refetch();
          }}
        >
          Refrescar
        </Button>
      </div>

      <Paragraph type="secondary">
        Monitoreo de jobs background. Los jobs se ejecutan automaticamente —
        usar esta pantalla para diagnosticar fallos. Hangfire dashboard completo
        disponible en <Text code>/hangfire</Text>.
      </Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Encolados"
              value={stats?.enqueued ?? 0}
              prefix={<ClockCircleOutlined />}
              loading={statsQuery.isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Procesando"
              value={stats?.processing ?? 0}
              prefix={<LoadingOutlined />}
              valueStyle={{ color: '#1677ff' }}
              loading={statsQuery.isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Exitosos (acumulado)"
              value={stats?.succeeded ?? 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
              loading={statsQuery.isLoading}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Fallidos"
              value={stats?.failed ?? 0}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: (stats?.failed ?? 0) > 0 ? '#cf1322' : undefined }}
              loading={statsQuery.isLoading}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeState}
          onChange={(k) => {
            setActiveState(k as StateKey);
            setPage(1);
          }}
          items={[
            { key: 'failed', label: <span><CloseCircleOutlined /> Fallidos</span> },
            { key: 'processing', label: <span><SyncOutlined spin /> Procesando</span> },
            { key: 'enqueued', label: <span><ClockCircleOutlined /> Encolados</span> },
            { key: 'scheduled', label: <span><ScheduleOutlined /> Programados</span> },
            { key: 'succeeded', label: <span><CheckCircleOutlined /> Exitosos</span> },
          ]}
        />

        <Table<JobItemDto>
          rowKey="id"
          loading={listQuery.isLoading}
          dataSource={listQuery.data?.items ?? []}
          locale={{
            emptyText: <Empty description={`No hay jobs en estado "${activeState}".`} />,
          }}
          pagination={{
            current: page,
            pageSize,
            total: listQuery.data?.total ?? 0,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: (total) => `${total} en total`,
          }}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              width: 80,
              render: (v) => <Text code style={{ fontSize: 11 }}>{v}</Text>,
            },
            {
              title: 'Job',
              key: 'job',
              render: (_, item) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{item.jobName ?? '(desconocido)'}</Text>
                  {item.arguments && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      args: {item.arguments}
                    </Text>
                  )}
                </Space>
              ),
            },
            {
              title: 'Cuando',
              key: 'time',
              width: 160,
              render: (_, item) => {
                const ts = item.stateChangedAt ?? item.createdAt;
                return ts ? (
                  <Tooltip title={dayjs(ts).format('YYYY-MM-DD HH:mm:ss')}>
                    {dayjs(ts).fromNow()}
                  </Tooltip>
                ) : '-';
              },
            },
            {
              title: 'Detalle',
              key: 'detail',
              render: (_, item) => {
                if (item.exceptionMessage) {
                  return (
                    <Tooltip title={item.exceptionMessage}>
                      <Tag color="error" style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {item.exceptionMessage.slice(0, 80)}
                      </Tag>
                    </Tooltip>
                  );
                }
                if (item.reason) return <Tag>{item.reason}</Tag>;
                if (item.server) return <Text type="secondary" style={{ fontSize: 11 }}>{item.server}</Text>;
                return null;
              },
            },
            {
              title: 'Acciones',
              key: 'actions',
              width: 140,
              render: (_, item) => {
                if (activeState === 'failed') {
                  return (
                    <Space>
                      <Button
                        size="small"
                        type="primary"
                        icon={<ReloadOutlined />}
                        loading={requeueMutation.isPending}
                        onClick={() => requeueMutation.mutate(item.id)}
                      >
                        Re-encolar
                      </Button>
                      <Popconfirm
                        title="Eliminar este job?"
                        onConfirm={() => deleteMutation.mutate(item.id)}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    </Space>
                  );
                }
                if (activeState === 'enqueued' || activeState === 'scheduled') {
                  return (
                    <Popconfirm
                      title="Eliminar este job?"
                      onConfirm={() => deleteMutation.mutate(item.id)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  );
                }
                return null;
              },
            },
          ]}
        />
      </Card>
    </div>
  );
}
